/**
 * Audio Recorder Hook
 *
 * Uses the MediaRecorder API to capture audio from the user's microphone.
 * Unlike useSpeechRecognition (text-only output via Web Speech API),
 * this hook produces an actual audio Blob for upload to the backend.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface AudioRecording {
  /** Audio data as a Blob */
  blob: Blob;
  /** Object URL for local playback */
  url: string;
  /** Duration in seconds */
  duration: number;
  /** File size in bytes */
  size: number;
  /** MIME type of the recording */
  mimeType: string;
  /** Generated filename */
  filename: string;
}

interface UseAudioRecorderReturn {
  /** Browser supports MediaRecorder */
  isSupported: boolean;
  /** Currently recording */
  isRecording: boolean;
  /** Live duration counter (seconds) during recording */
  duration: number;
  /** Completed recording (null until stopped) */
  recording: AudioRecording | null;
  /** Start recording from microphone */
  startRecording: () => Promise<void>;
  /** Stop recording and produce AudioRecording */
  stopRecording: () => void;
  /** Discard current recording */
  clearRecording: () => void;
  /** Error message */
  error: string | null;
}

/**
 * Determine the best supported MIME type for audio recording.
 * Prefer WebM/Opus (Chrome/Edge/Firefox), fall back to MP4 (Safari).
 */
function getPreferredMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];

  for (const mime of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }

  return ''; // Let browser pick default
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [recording, setRecording] = useState<AudioRecording | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const isSupported =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof MediaRecorder !== 'undefined';

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (recording?.url) {
        URL.revokeObjectURL(recording.url);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearRecording = useCallback(() => {
    if (recording?.url) {
      URL.revokeObjectURL(recording.url);
    }
    setRecording(null);
    setDuration(0);
    setError(null);
  }, [recording]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Stream tracks are stopped in onstop handler
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Audio recording not supported in this browser');
      return;
    }

    // Clear previous recording
    if (recording?.url) {
      URL.revokeObjectURL(recording.url);
    }
    setRecording(null);
    setError(null);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getPreferredMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        // Stop all tracks to release the mic
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;

        const finalDuration = Math.round((Date.now() - startTimeRef.current) / 1000);
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });

        const ext = recorder.mimeType?.includes('mp4') ? 'mp4' : 'webm';
        const timestamp = Date.now().toString(36);
        const filename = `recording-${timestamp}.${ext}`;

        const url = URL.createObjectURL(blob);

        setRecording({
          blob,
          url,
          duration: finalDuration,
          size: blob.size,
          mimeType: recorder.mimeType || 'audio/webm',
          filename,
        });
        setIsRecording(false);
      };

      recorder.onerror = () => {
        setError('Recording failed');
        setIsRecording(false);
        stream.getTracks().forEach((track) => track.stop());
      };

      // Start recording with 250ms timeslice for chunk collection
      recorder.start(250);
      startTimeRef.current = Date.now();
      setIsRecording(true);
      setDuration(0);

      // Live duration counter
      timerRef.current = setInterval(() => {
        setDuration(Math.round((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch (err) {
      const messages: Record<string, string> = {
        NotAllowedError: 'Microphone access denied',
        NotFoundError: 'No microphone found',
        NotReadableError: 'Microphone is in use by another application',
      };
      const name = err instanceof DOMException ? err.name : '';
      setError(messages[name] || 'Failed to start recording');
    }
  }, [isSupported, recording]);

  return {
    isSupported,
    isRecording,
    duration,
    recording,
    startRecording,
    stopRecording,
    clearRecording,
    error,
  };
}
